/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/Actions/EndpointsEditorActionsCreator" />
import { EndpointsEditorActionsHub, ErrorDetails } from "DistributedTaskUI/ServiceEndpoints/Editor/Actions/EndpointsEditorActionsHub";
import { ServiceEndpointsSource } from "DistributedTaskUI/ServiceEndpoints/Editor/Source/ServiceEndpointsSource";
import { ActionsHubManager } from "DistributedTask/Flux/ActionsHubManager";
import { ActionCreatorBase } from "DistributedTask/Flux/Actions";
import { Permission, ResourcePipelinePermissions } from "PipelineChecks/Authorization/Client/Contracts/PipelinePermissions";
import { ServiceEndpointType, ServiceEndpoint, DataSourceDetails, ServiceEndpointRequest, ServiceEndpointRequestResult, ResultTransformationDetails, OAuthConfiguration } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { PipelinesAuthorizationSource } from "DistributedTaskUI/SecurityComponent/PipelinePermissionsAuthorization/Source/PipelinesAuthorizationSource";
import { EndpointOperationStatusState } from "DistributedTaskUI/ServiceEndpoints/Editor/Constants";
import * as DelayedFunction from "DistributedTask/Common/AadAuthorizer/DelayedFunction";
import { IVssPageContext } from "VSS/Platform/Context";
import * as Utils_Accessibility from "VSS/Core/Util/Accessibility";
import * as EndpointConstants from "ServiceEndpoints/Client/Constants/ServiceEndpoint";
import * as Utils_String from "VSS/Core/Util/String";
import { IdentityType } from "../UIContributions/AzureRMEndpoint/Contracts/IdentityType";
import { AzureEndpointConstants, EndpointAuthorizationSchemes } from "ServiceEndpoints/Client/Constants/ServiceEndpoint";
import { ArmCredentialsType } from "../UIContributions/AzureRMEndpoint/Components/AzureRMCredentialsTypeSelector";

export class EndpointsEditorActionsCreator extends ActionCreatorBase {
    constructor(context: IVssPageContext) {
        super();
        this._source = ServiceEndpointsSource.getInstance(context);
        this._actions = ActionsHubManager.GetActionsHub<EndpointsEditorActionsHub>(EndpointsEditorActionsHub);
        this._pipelinesAuthorizationSource = new PipelinesAuthorizationSource(context);
    }

    public initialize(): void {
        this._actions = ActionsHubManager.GetActionsHub<EndpointsEditorActionsHub>(EndpointsEditorActionsHub);
    }

    public static getKey(): string {
        return "endpoints-editor-actionscreator";
    }

    public initializeServiceEndpointType(endpointType: ServiceEndpointType): void {
        if (endpointType) {
            this._actions.setServiceEndpointType.invoke(endpointType);

            this._source.getOAuthConfigurations(endpointType).then((configurations: OAuthConfiguration[]) => {
                if (configurations && configurations.length > 0) {
                    this._actions.setOAuthConfigurations.invoke(configurations);
                }
            });
        }
    }

    public setServiceEndpointIdentityType(identityType: IdentityType): void {
        switch (identityType) {
            case IdentityType.AppRegistrationAutomatic:
                this.initializeServiceEndpointCreationMode(AzureEndpointConstants.SpnCreationModeAutomatic);
                this.initializeServiceEndpointAuthenticationScheme(EndpointAuthorizationSchemes.WorkloadIdentityFederation);
                break;
            case IdentityType.AppRegistrationManual:
                this.initializeServiceEndpointCreationMode(AzureEndpointConstants.SpnCreationModeManual);
                this.initializeServiceEndpointAuthenticationScheme(EndpointAuthorizationSchemes.WorkloadIdentityFederation);
                break;
            case IdentityType.ManagedIdentityAgentAssigned:
                this.removeServiceEndpointCreationMode();
                this.initializeServiceEndpointAuthenticationScheme(EndpointAuthorizationSchemes.ManagedServiceIdentity);
                break;
            case IdentityType.PublishProfile:
                this.removeServiceEndpointCreationMode();
                this.initializeServiceEndpointAuthenticationScheme(EndpointAuthorizationSchemes.PublishProfile);
                break;
        }
    }

    public setServiceEndpointCrentialType(credentialType: ArmCredentialsType) {
        switch (credentialType) {
            case ArmCredentialsType.Secret:
                this.initializeServiceEndpointAuthenticationScheme(EndpointAuthorizationSchemes.ServicePrincipal);
                break;
            case ArmCredentialsType.Wif:
                this.initializeServiceEndpointAuthenticationScheme(EndpointAuthorizationSchemes.WorkloadIdentityFederation);
                break;
        }
    }

    public initializeServiceEndpointAuthenticationScheme(authScheme: string): void {
        if (authScheme) {
            this._actions.setAuthenticationScheme.invoke(authScheme);
        }
    }

    public initializeServiceEndpointCreationMode(creationMode: string): void {
        if (creationMode) {
            this._actions.setEndpointCreationMode.invoke(creationMode);
        }
    }

    public removeServiceEndpointCreationMode(): void {
            this._actions.removeEndpointCreationMode.invoke();
    }

    public initializeServiceEndpointDraftMode(draftMode: boolean): void {
         this._actions.setEndpointDraftMode.invoke(draftMode);
    }

    public setServiceEndpoint(endpoint: ServiceEndpoint) {
        this._actions.setServiceEndpoint.invoke(endpoint);
    }

    public saveServiceEndpoint(endpoint: ServiceEndpoint): Promise<ServiceEndpoint> {
        return this._source.createServiceEndpoint(endpoint);
    }

    public saveResourceAuthorizationState(serviceEndpointId: string, isAuthorizedForAllDefinitions: boolean) {
        let resource = {
            id: serviceEndpointId,
            type: this._endpointResourceType,
            name: ""
        };

        let allPipelines: Permission = {
            authorized: isAuthorizedForAllDefinitions,
            authorizedBy: null,
            authorizedOn: null
        };

        let resourceAuthorizedDefinitions: ResourcePipelinePermissions = {
            resource: resource,
            pipelines: [],
            allPipelines: allPipelines
        };

        this._pipelinesAuthorizationSource.updatePipelinePermisionsForResource(
            this._endpointResourceType,
            serviceEndpointId,
            resourceAuthorizedDefinitions
        );
    }

    public updateOpenToAllPipelinesFlag(openToAllPipelinesFlag: boolean): void {
        this._actions.updateOpenToAllPipelinesFlag.invoke(openToAllPipelinesFlag);
    }

    public verifyServiceEndpoint(serviceEndpointRequest: ServiceEndpointRequest, serviceEndpointId: string): Promise<ServiceEndpointRequestResult> {
        return this._source.verifyServiceEndpoint(serviceEndpointRequest, serviceEndpointId);
    }

    public verifyAndSaveServiceEndpoint(
        serviceEndpoint: ServiceEndpoint,
        dataSourceName: string,
        openToAllPipelinesFlag: boolean,
        isUpdate: boolean
    ): void {
        let dataSourceDetails: DataSourceDetails = {
            dataSourceName: dataSourceName,
            dataSourceUrl: "",
            headers: null,
            resourceUrl: "",
            requestContent: null,
            requestVerb: null,
            parameters: null,
            resultSelector: "",
            initialContextTemplate: ""
        };

        let resultTransformationDetails: ResultTransformationDetails = {
            callbackContextTemplate: "",
            callbackRequiredTemplate: "",
            resultTemplate: ""
        };

        let serviceEndpointRequest: ServiceEndpointRequest = {
            dataSourceDetails: dataSourceDetails,
            resultTransformationDetails: resultTransformationDetails,
            serviceEndpointDetails: serviceEndpoint
        };

        this._actions.updateSaveProgress.invoke({ verifyInProgress: true, errorMessage: "" });

        this._source.verifyServiceEndpoint(serviceEndpointRequest, serviceEndpoint.id).then(
            (result: ServiceEndpointRequestResult) => {
                if (result.errorMessage) {
                    this._actions.updateSaveProgress.invoke({ verifyInProgress: false, errorMessage: result.errorMessage, activityId: result.activityId });
                } else {
                    this.customSaveServiceEndpoint(serviceEndpoint, openToAllPipelinesFlag, isUpdate);
                }
            },
            (error) => {
                Utils_Accessibility.announce(error.message);
                this._actions.updateSaveProgress.invoke({
                    verifyInProgress: false,
                    errorMessage: error.message ? error.message : JSON.stringify(error)
                });
            }
        );
    }

    public customSaveServiceEndpoint(endpoint: ServiceEndpoint, openToAllPipelinesFlag: boolean, isUpdate: boolean): Promise<void> {
        this._actions.updateSaveProgress.invoke({ saveInProgress: true, verifyInProgress: false, errorMessage: "" });
        let saveEndpointPromise = isUpdate ? this._source.updateServiceEndpoint(endpoint) : this._source.createServiceEndpoint(endpoint);
        return saveEndpointPromise.then(
            (result: ServiceEndpoint) => {
                this.saveResourceAuthorizationState(result.id, openToAllPipelinesFlag);
                if (!result.isReady && !this._isDraft(result)) {
                    this._monitorServiceEndpointProgress(result.id, isUpdate);
                } else {
                    if (this._isDraft(result)) {
                        this._actions.setServiceEndpoint.invoke(result);
                    } else {
                        this._actions.setServiceEndpoint.invoke(endpoint);
                    }
                    this._actions.updateSaveProgress.invoke({ saveInProgress: false });
                }
            },
            (error) => {
                Utils_Accessibility.announce(error.message);
                this._actions.updateSaveProgress.invoke({
                    saveInProgress: false,
                    errorMessage: error.message ? error.message : JSON.stringify(error)
                });
            }
        );
    }

    public initializeEditState(serviceEndpoint: ServiceEndpoint) {
        this._source.getServiceEndpointType(serviceEndpoint.type).then((endpointTypes: ServiceEndpointType[]) => {
            if (endpointTypes && endpointTypes.length > 0) {
                this._actions.setServiceEndpointType.invoke(endpointTypes[0]);
                this._actions.initializeStateEndpoint.invoke(serviceEndpoint);

                this._source.getOAuthConfigurations(endpointTypes[0]).then((configurations: OAuthConfiguration[]) => {
                    if (configurations) {
                        this._actions.setOAuthConfigurations.invoke(configurations);
                    }
                });
            }
        });

        this._pipelinesAuthorizationSource
            .getPipelinePermissionsForResource(this._endpointResourceType, serviceEndpoint.id)
            .then((permissions: ResourcePipelinePermissions) => {
                this._actions.updateOpenToAllPipelinesFlag.invoke(permissions.allPipelines ? permissions.allPipelines.authorized : false);
            });
    }

    public updateServiceEndpoint(endpoint: ServiceEndpoint): Promise<ServiceEndpoint> {
        return this._source.updateServiceEndpoint(endpoint);
    }

    public setErrorDetails(details: ErrorDetails): void {
        this._actions.setErrorDetails.invoke(details);
    }

    public clearErrorDetails(): void {
        this._actions.setErrorDetails.invoke({});
    }

    private _monitorServiceEndpointProgress(endpointId: string, isUpdate: boolean): void {
        this._actions.updateSaveProgress.invoke({ setupInProgress: true, saveInProgress: false, errorMessage: "" });
        let operationStatus = { state: EndpointOperationStatusState.InProgress, statusMessage: "" };
        let monitorServiceAccountSetupProgress = new DelayedFunction.DelayedFunction(this, 1000, "monitorServiceAccountSetupProgress", () => {
            this._source.getServiceEndpoint(endpointId).then((response: ServiceEndpoint) => {
                if (response.operationStatus !== null && response.operationStatus !== undefined) {
                    operationStatus = response.operationStatus;
                }

                if (response.isReady) {
                    this._actions.setServiceEndpoint.invoke(response);
                    this._actions.updateSaveProgress.invoke({ setupInProgress: false });
                    monitorServiceAccountSetupProgress.cancel();
                } else if (operationStatus.state.toLowerCase() === EndpointOperationStatusState.Failed) {
                    if (!isUpdate) {
                        this._source.deleteServiceEndpoint(endpointId);
                    }

                    this._actions.updateSaveProgress.invoke({ setupInProgress: false, errorMessage: operationStatus.statusMessage });
                    monitorServiceAccountSetupProgress.cancel();
                } else if (this._isDraft(response)) {
                    this._actions.setServiceEndpoint.invoke(response);
                    this._actions.updateSaveProgress.invoke({ setupInProgress: false });
                    monitorServiceAccountSetupProgress.cancel();
                } else {
                    monitorServiceAccountSetupProgress.reset();
                }
            });
        });

        monitorServiceAccountSetupProgress.start();
    }

    private _isDraft = (endpoint: ServiceEndpoint) => Utils_String.equals(endpoint.data[EndpointConstants.AzureEndpointConstants.IsDraft], "true", true);

    private _actions: EndpointsEditorActionsHub;
    private _source: ServiceEndpointsSource;
    private _pipelinesAuthorizationSource: PipelinesAuthorizationSource;
    private _endpointResourceType: string = "endpoint";
}
